import {
  type BoardTile,
  type Coordinate,
  calculateIncome,
  canProduceGrade,
  cellKey,
  countControlledCapturePoints,
  FACTIONS,
  fieldLimitForUnit,
  type GameState,
  getUnitsAt,
  legalDestinationsForUnit,
  type PlayerState,
  resolveUnitStats,
  TECH_CARD_DEFINITIONS,
  type TechCardId,
  type TriAngerAction,
  UNIT_DEFINITIONS,
  type UnitInstance,
} from "@cloudnest/tri-anger-engine";
import {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import {
  getParticipantDisplayName,
  getParticipantDisplayNames,
} from "../../shared/utils/participants";
import {
  triAngerMultiplayerActions,
  useTriAngerMultiplayer,
} from "./multiplayer-game-store";

type SelectedAction =
  | { readonly kind: "idle" }
  | { readonly kind: "move"; readonly unitId: string };

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { gameState, lastError, myPlayerId } = useTriAngerMultiplayer();
  const [selectedAction, setSelectedAction] = useState<SelectedAction>({
    kind: "idle",
  });
  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;
  const actionsRef = useRef(triAngerMultiplayerActions);
  const lobbyActionsRef = useRef(lobbyActions);

  useEffect(() => {
    actionsRef.current = triAngerMultiplayerActions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: (eventType, payload, seq) => {
      actionsRef.current.applyRemoteEvent(eventType, payload, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialize once when entering a concrete room/user role; room_updated events create new objects and should not reinitialize the game.
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;
    if (isHost) {
      const playerIds = currentRoom.participantUserIds ?? [];
      const playerNames = getParticipantDisplayNames(currentRoom, "Player");
      void triAngerMultiplayerActions.initializeAsHost(
        roomId,
        playerIds,
        playerNames,
        currentRoom.config,
      );
    } else {
      void triAngerMultiplayerActions.initializeAsGuest(roomId);
    }
    return () => {
      triAngerMultiplayerActions.reset();
    };
  }, [roomId, currentRoom?.id, user?.id, isHost]);

  useEffect(() => {
    if (!roomId) return;
    const id = setInterval(() => {
      void triAngerMultiplayerActions.catchUpEvents(roomId);
    }, 4000);
    return () => clearInterval(id);
  }, [roomId]);

  const displayNames = useMemo(() => {
    if (!currentRoom) return {};
    return Object.fromEntries(
      (currentRoom.participantUserIds ?? []).map((playerId, index) => [
        playerId,
        getParticipantDisplayName(currentRoom, playerId, `Player ${index + 1}`),
      ]),
    );
  }, [currentRoom]);

  function leaveGame(): void {
    triAngerMultiplayerActions.reset();
    void lobbyActions.leaveGame();
    navigateTo("/tri-anger/lobby");
  }

  async function submit(action: TriAngerAction): Promise<void> {
    await triAngerMultiplayerActions.submitAction(action);
    if (action.type === "MoveUnit") setSelectedAction({ kind: "idle" });
  }

  if (!currentRoom || !gameState) {
    return (
      <div className="ta-client-shell ta-client-center">
        <section className="ta-client-panel">
          <p className="ta-client-kicker">Tri-Anger</p>
          <h1>게임 로딩 중</h1>
          <p>{connectionStatus === "connected" ? "준비 중" : "연결 중"}</p>
        </section>
      </div>
    );
  }

  const currentPlayer = gameState.players.find(
    (player) => player.id === gameState.currentPlayerId,
  );
  const isMyTurn =
    myPlayerId !== null &&
    gameState.currentPlayerId === myPlayerId &&
    gameState.phase !== "GAME_OVER";

  return (
    <div className="ta-client-shell" data-testid="tri-anger-client-screen">
      <header className="ta-client-topbar" data-testid="tri-anger-topbar">
        <div className="ta-client-brand">
          <strong>Tri-Anger</strong>
          <span>
            {phaseLabel(gameState.phase)} · Turn {gameState.turn} ·{" "}
            {currentPlayer ? displayNames[currentPlayer.id] : "-"} ·{" "}
            {connectionStatus}
          </span>
        </div>
        <div className="ta-client-player-strip">
          {gameState.players.map((player) => (
            <PlayerPill
              key={player.id}
              active={player.id === gameState.currentPlayerId}
              displayName={displayNames[player.id] ?? player.name}
              player={player}
              state={gameState}
            />
          ))}
        </div>
        <button
          className="ta-client-ghost-button"
          type="button"
          onClick={leaveGame}
        >
          나가기
        </button>
      </header>

      {lastError ? (
        <div className="ta-client-error" data-testid="tri-anger-error">
          <span>{lastError.error}</span>
          <button
            className="ta-client-ghost-button"
            type="button"
            onClick={() => triAngerMultiplayerActions.clearError()}
          >
            닫기
          </button>
        </div>
      ) : null}

      <main className="ta-client-main">
        <section className="ta-client-board-wrap">
          <Board
            isMyTurn={isMyTurn}
            onSubmit={submit}
            selectedAction={selectedAction}
            setSelectedAction={setSelectedAction}
            state={gameState}
          />
        </section>
        <aside className="ta-client-side">
          <PlayerDetails
            displayNames={displayNames}
            myPlayerId={myPlayerId}
            state={gameState}
          />
          <LogPanel state={gameState} />
        </aside>
      </main>

      <ActionDock
        isMyTurn={isMyTurn}
        myPlayerId={myPlayerId}
        onSubmit={submit}
        selectedAction={selectedAction}
        setSelectedAction={setSelectedAction}
        state={gameState}
      />
    </div>
  );
}

function PlayerPill({
  active,
  displayName,
  player,
  state,
}: {
  readonly active: boolean;
  readonly displayName: string;
  readonly player: PlayerState;
  readonly state: GameState;
}) {
  const faction = FACTIONS[player.faction];
  return (
    <div
      className="ta-client-player-pill"
      data-active={active}
      style={{ "--ta-player-color": faction.color } as CSSProperties}
    >
      <span className="ta-client-player-name">{displayName}</span>
      <span>{player.gold}G</span>
      <span>{player.techCoins}T</span>
      <span>{countControlledCapturePoints(state, player.id)}/8</span>
    </div>
  );
}

function Board({
  isMyTurn,
  onSubmit,
  selectedAction,
  setSelectedAction,
  state,
}: {
  readonly isMyTurn: boolean;
  readonly onSubmit: (action: TriAngerAction) => Promise<void>;
  readonly selectedAction: SelectedAction;
  readonly setSelectedAction: (action: SelectedAction) => void;
  readonly state: GameState;
}) {
  const legalMoves =
    selectedAction.kind === "move"
      ? legalDestinationsForUnit(state, selectedAction.unitId)
      : [];

  function handleCellClick(tile: BoardTile): void {
    if (!isMyTurn || state.phase !== "MOVE") return;
    if (
      selectedAction.kind === "move" &&
      legalMoves.some((position) => sameCoord(position, tile.position))
    ) {
      void onSubmit({
        type: "MoveUnit",
        playerId: state.currentPlayerId,
        unitId: selectedAction.unitId,
        to: tile.position,
      });
      return;
    }
    const ownUnit = getUnitsAt(state, tile.position).find(
      (unit) => unit.ownerId === state.currentPlayerId,
    );
    if (ownUnit) {
      setSelectedAction({ kind: "move", unitId: ownUnit.id });
    }
  }

  return (
    <div className="ta-client-board" data-testid="tri-anger-board">
      {state.board.map((tile) => {
        const units = getUnitsAt(state, tile.position);
        const isLegal =
          selectedAction.kind === "move" &&
          legalMoves.some((position) => sameCoord(position, tile.position));
        return (
          <button
            key={cellKey(tile.position)}
            className="ta-client-cell"
            data-kind={tile.kind}
            data-legal={isLegal}
            data-testid={`tri-anger-cell-${tile.position.x}-${tile.position.y}`}
            type="button"
            onClick={() => handleCellClick(tile)}
          >
            <span className="ta-client-cell-label">{tileLabel(tile)}</span>
            <span className="ta-client-cell-control">
              {state.controlByCell[cellKey(tile.position)]
                ? "●"
                : tile.kind === "multi" &&
                    (state.constructionByCell[cellKey(tile.position)] ?? 0) > 0
                  ? `${state.constructionByCell[cellKey(tile.position)]}/3`
                  : ""}
            </span>
            <span className="ta-client-unit-stack">
              {units.slice(0, 4).map((unit) => (
                <UnitChip key={unit.id} state={state} unit={unit} />
              ))}
              {units.length > 4 ? (
                <span className="ta-client-unit-more">+{units.length - 4}</span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function UnitChip({
  state,
  unit,
}: {
  readonly state: GameState;
  readonly unit: UnitInstance;
}) {
  const definition = UNIT_DEFINITIONS[unit.typeId];
  const owner = state.players.find((player) => player.id === unit.ownerId);
  const faction = owner ? FACTIONS[owner.faction] : undefined;
  const stats = resolveUnitStats(state, unit);
  return (
    <span
      className="ta-client-unit-chip"
      style={
        { "--ta-unit-color": faction?.color ?? "#64748b" } as CSSProperties
      }
      title={`${definition.name} HP ${unit.hp}/${stats.hp}`}
    >
      {definition.name.slice(0, 1)}
      <small>{unit.hp}</small>
    </span>
  );
}

function PlayerDetails({
  displayNames,
  myPlayerId,
  state,
}: {
  readonly displayNames: Readonly<Record<string, string>>;
  readonly myPlayerId: string | null;
  readonly state: GameState;
}) {
  return (
    <section className="ta-client-panel">
      <h2>Players</h2>
      <div className="ta-client-player-details">
        {state.players.map((player) => (
          <article key={player.id} className="ta-client-player-card">
            <div className="ta-client-player-card-head">
              <strong>{displayNames[player.id] ?? player.name}</strong>
              <span>{player.id === myPlayerId ? "You" : player.faction}</span>
            </div>
            <div className="ta-client-stat-row">
              <span>Gold {player.gold}</span>
              <span>Income {calculateIncome(state, player.id)}</span>
              <span>Tech {player.techCoins}</span>
            </div>
            <div className="ta-client-tech-row">
              {player.ownedTech.length === 0 ? (
                <span className="ta-client-muted">No tech</span>
              ) : (
                player.ownedTech.map((tech) => (
                  <span key={tech.id} data-revealed={tech.revealed}>
                    {TECH_CARD_DEFINITIONS[tech.id].name}
                  </span>
                ))
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function LogPanel({ state }: { readonly state: GameState }) {
  const entries = [...state.log].slice(-8).reverse();
  return (
    <section className="ta-client-panel ta-client-log">
      <h2>Log</h2>
      {entries.map((entry, index) => (
        <p key={`${entry.turn}-${entry.phase}-${index}`}>
          <span>{entry.phase}</span>
          {entry.message}
        </p>
      ))}
    </section>
  );
}

function ActionDock({
  isMyTurn,
  myPlayerId,
  onSubmit,
  selectedAction,
  setSelectedAction,
  state,
}: {
  readonly isMyTurn: boolean;
  readonly myPlayerId: string | null;
  readonly onSubmit: (action: TriAngerAction) => Promise<void>;
  readonly selectedAction: SelectedAction;
  readonly setSelectedAction: (action: SelectedAction) => void;
  readonly state: GameState;
}) {
  if (state.phase === "GAME_OVER") {
    const winner = state.players.find((player) => player.id === state.winnerId);
    return (
      <footer
        className="ta-client-action-dock"
        data-testid="tri-anger-action-dock"
      >
        <strong>{winner?.name ?? "Winner"}</strong>
        <span>{state.victoryCondition}</span>
      </footer>
    );
  }

  const player = state.players.find((candidate) => candidate.id === myPlayerId);
  const canAct = isMyTurn && player !== undefined;

  return (
    <footer
      className="ta-client-action-dock"
      data-testid="tri-anger-action-dock"
    >
      <div className="ta-client-action-heading">
        <strong>{phaseLabel(state.phase)}</strong>
        <span>{canAct ? "Your turn" : "Waiting"}</span>
      </div>
      {canAct && player && state.phase === "MOVE" ? (
        <MoveControls
          onSubmit={onSubmit}
          selectedAction={selectedAction}
          setSelectedAction={setSelectedAction}
          state={state}
        />
      ) : null}
      {canAct && player && state.phase === "PRODUCE" ? (
        <ProduceControls onSubmit={onSubmit} player={player} state={state} />
      ) : null}
      {canAct && !["MOVE", "PRODUCE"].includes(state.phase) ? (
        <button
          className="ta-client-primary-button"
          type="button"
          onClick={() =>
            void onSubmit({ type: "EndPhase", playerId: state.currentPlayerId })
          }
        >
          Resolve
        </button>
      ) : null}
      {canAct ? (
        <button
          className="ta-client-ghost-button"
          type="button"
          onClick={() =>
            void onSubmit({
              type: "Surrender",
              playerId: state.currentPlayerId,
            })
          }
        >
          Surrender
        </button>
      ) : null}
    </footer>
  );
}

function MoveControls({
  onSubmit,
  selectedAction,
  setSelectedAction,
  state,
}: {
  readonly onSubmit: (action: TriAngerAction) => Promise<void>;
  readonly selectedAction: SelectedAction;
  readonly setSelectedAction: (action: SelectedAction) => void;
  readonly state: GameState;
}) {
  const units = state.units.filter(
    (unit) => unit.ownerId === state.currentPlayerId,
  );
  const selectedUnit =
    selectedAction.kind === "move"
      ? state.units.find((unit) => unit.id === selectedAction.unitId)
      : undefined;
  const destinations = selectedUnit
    ? legalDestinationsForUnit(state, selectedUnit.id).slice(0, 8)
    : [];

  return (
    <div className="ta-client-action-grid">
      <select
        aria-label="Move unit"
        value={selectedUnit?.id ?? ""}
        onChange={(event) =>
          setSelectedAction(
            event.currentTarget.value
              ? { kind: "move", unitId: event.currentTarget.value }
              : { kind: "idle" },
          )
        }
      >
        <option value="">Unit</option>
        {units.map((unit) => (
          <option key={unit.id} value={unit.id}>
            {UNIT_DEFINITIONS[unit.typeId].name} ({unit.position.x + 1},
            {unit.position.y + 1})
          </option>
        ))}
      </select>
      {destinations.map((to) => (
        <button
          key={cellKey(to)}
          className="ta-client-secondary-button"
          type="button"
          onClick={() =>
            selectedUnit
              ? void onSubmit({
                  type: "MoveUnit",
                  playerId: state.currentPlayerId,
                  unitId: selectedUnit.id,
                  to,
                })
              : undefined
          }
        >
          {to.x + 1},{to.y + 1}
        </button>
      ))}
      <button
        className="ta-client-primary-button"
        type="button"
        onClick={() =>
          void onSubmit({ type: "EndPhase", playerId: state.currentPlayerId })
        }
      >
        End Move
      </button>
    </div>
  );
}

function ProduceControls({
  onSubmit,
  player,
  state,
}: {
  readonly onSubmit: (action: TriAngerAction) => Promise<void>;
  readonly player: PlayerState;
  readonly state: GameState;
}) {
  const worker = state.units.find(
    (unit) => unit.ownerId === player.id && unit.typeId === "WORKER",
  );
  const units = [
    ...player.unitDeck,
    ...(worker && player.faction === "ECSU"
      ? (["BOMB", "TOWER"] as const)
      : []),
  ];
  const buildTargets = state.board.filter(
    (tile) =>
      tile.kind === "multi" &&
      getUnitsAt(state, tile.position).some(
        (unit) => unit.ownerId === player.id,
      ),
  );
  const ownedIds = new Set(player.ownedTech.map((tech) => tech.id));
  const buyableTech = player.techDeck.filter((techId) => !ownedIds.has(techId));
  const revealableTech = player.ownedTech.filter((tech) => !tech.revealed);

  return (
    <div className="ta-client-produce-stack">
      <div className="ta-client-action-grid">
        {units.map((unitTypeId) => {
          const definition = UNIT_DEFINITIONS[unitTypeId];
          const fielded = state.units.filter(
            (unit) => unit.ownerId === player.id && unit.typeId === unitTypeId,
          ).length;
          const disabled =
            player.gold < definition.cost ||
            !canProduceGrade(player, definition.grade) ||
            fielded >= fieldLimitForUnit(unitTypeId) ||
            (definition.structure && !worker);
          return (
            <button
              key={unitTypeId}
              className="ta-client-secondary-button"
              disabled={disabled}
              type="button"
              onClick={() =>
                void onSubmit({
                  type: "ProduceUnit",
                  playerId: player.id,
                  unitTypeId,
                  workerUnitId: definition.structure ? worker?.id : undefined,
                })
              }
            >
              {definition.name} {definition.cost}G
            </button>
          );
        })}
      </div>
      <div className="ta-client-action-grid">
        {buildTargets.map((tile) => (
          <button
            key={cellKey(tile.position)}
            className="ta-client-secondary-button"
            disabled={player.gold < 5}
            type="button"
            onClick={() =>
              void onSubmit({
                type: "BuildMulti",
                playerId: player.id,
                at: tile.position,
              })
            }
          >
            M {tile.position.x + 1},{tile.position.y + 1}
          </button>
        ))}
        <button
          className="ta-client-secondary-button"
          disabled={player.gold < 8}
          type="button"
          onClick={() =>
            void onSubmit({ type: "BuyTechCoin", playerId: player.id })
          }
        >
          Tech Coin 8G
        </button>
      </div>
      <TechControls
        buyableTech={buyableTech}
        onSubmit={onSubmit}
        player={player}
        revealableTech={revealableTech}
      />
      <button
        className="ta-client-primary-button"
        type="button"
        onClick={() => void onSubmit({ type: "EndPhase", playerId: player.id })}
      >
        End Produce
      </button>
    </div>
  );
}

function TechControls({
  buyableTech,
  onSubmit,
  player,
  revealableTech,
}: {
  readonly buyableTech: readonly TechCardId[];
  readonly onSubmit: (action: TriAngerAction) => Promise<void>;
  readonly player: PlayerState;
  readonly revealableTech: readonly { readonly id: TechCardId }[];
}) {
  return (
    <div className="ta-client-action-grid">
      {buyableTech.slice(0, 4).map((techId) => {
        const tech = TECH_CARD_DEFINITIONS[techId];
        const cost = tech.grade === "mid" ? 2 : 3;
        return (
          <button
            key={techId}
            className="ta-client-secondary-button"
            disabled={player.techCoins < cost}
            type="button"
            onClick={() =>
              void onSubmit({
                type: "BuyTechCard",
                playerId: player.id,
                techCardId: techId,
              })
            }
          >
            {tech.name} {cost}T
          </button>
        );
      })}
      {revealableTech.slice(0, 3).map((tech) => (
        <button
          key={tech.id}
          className="ta-client-secondary-button"
          type="button"
          onClick={() =>
            void onSubmit({
              type: "RevealTechCard",
              playerId: player.id,
              techCardId: tech.id,
            })
          }
        >
          Reveal {TECH_CARD_DEFINITIONS[tech.id].name}
        </button>
      ))}
    </div>
  );
}

function tileLabel(tile: BoardTile): string {
  if (tile.kind === "home") return "H";
  if (tile.kind === "multi") return "M";
  if (tile.kind === "blocked") return "X";
  if (tile.kind === "capture") return String(tile.income);
  return "";
}

function phaseLabel(phase: GameState["phase"]): string {
  switch (phase) {
    case "MOVE":
      return "Move";
    case "PRODUCE":
      return "Produce";
    case "COMBAT":
      return "Combat";
    case "OCCUPY":
      return "Occupy";
    case "INCOME":
      return "Income";
    case "GAME_OVER":
      return "Game Over";
  }
}

function sameCoord(a: Coordinate, b: Coordinate): boolean {
  return a.x === b.x && a.y === b.y;
}
